/**
 * Team Leave Requests Hook
 *
 * Loads the leave requests submitted by the current manager's team, for the
 * "View Requests / Approvals" tab, and exposes approve / reject mutations.
 *
 * Data flow (mirrors LeaveContext.fetchMyLeaveRequests, which is the proven
 * path for the same entities):
 *   1. Team roster comes from useTeamMembers (EssWorkerManagerRelations).
 *   2. Per member, LeaveRequestsV2 returns one record PER DAY — these are
 *      grouped by RequestId into a single request with per-day leave lines.
 *   3. The authoritative status is read from EssLeaveRequestHeaders
 *      (LeaveStatus) per RequestId.
 *   4. Leave type Description + unit (Days/Hours) come from the LeaveTypes
 *      lookup.
 *
 * Approve / reject PATCH EssLeaveRequestHeaders LeaveStatus and refetch.
 *
 * Returned request shape matches ManagerTeamMember's approvals list and
 * LeaveRequestDetailModal:
 *   { id, RequestId, dataAreaId, employeeName, employeeNo,
 *     leaveTypeCode, leaveTypeName, leaveTypeDescription, unit,
 *     requestedBy, status, reason, requestedDate,
 *     startDate, endDate, days, totalHours,
 *     leaveLines: [{ date, amount }] }
 */
import { useQuery, useMutation, useQueryClient } from "react-query";
import { leaveService } from "../../api/services";
import useTeamMembers from "./useTeamMembers";

/**
 * Sick Leave has its own dedicated approval flow and must not appear in the
 * manager's View Requests / Approvals list. Matches on both the LeaveType
 * code and its resolved Description, mirroring the lowerType.includes('sick')
 * convention already used for live LeaveTypes data elsewhere (e.g.
 * ManagerPersonalLeave.jsx, EmployeeLeaveManagement.jsx).
 */
const isSickLeave = (leaveType, descMap = {}) => {
  const code = (leaveType || "").toLowerCase();
  const description = (descMap[leaveType] || "").toLowerCase();
  return code.includes("sick") || description.includes("sick");
};

/**
 * Group per-day LeaveRequestsV2 records into one request per RequestId.
 * @param {Array} records - Raw LeaveRequestsV2 rows for a single employee
 * @param {Object} unitMap - LeaveType -> LeaveAmountUnit ('Hours' | 'Days')
 * @param {Object} descMap - LeaveType -> Description
 */
const groupByRequestId = (records, unitMap = {}, descMap = {}) => {
  const grouped = {};

  records.forEach((r) => {
    const requestId = r.RequestId;
    if (!requestId) return;

    if (!grouped[requestId]) {
      grouped[requestId] = {
        RequestId: requestId,
        dataAreaId: r.dataAreaId || "usmf",
        leaveType: r.LeaveType,
        leaveTypeDescription: descMap[r.LeaveType] || r.LeaveType,
        unit: unitMap[r.LeaveType] === "Hours" ? "Hours" : "Days",
        status: r.Status,
        reason: r.Comment || "",
        requestedDate: r.RequestDate,
        personnelNumber: r.PersonnelNumber,
        lines: [],
        totalAmount: 0,
      };
    }

    grouped[requestId].lines.push({
      date: r.LeaveDate,
      amount: r.Amount || 0,
    });
    grouped[requestId].totalAmount += r.Amount || 0;
  });

  return Object.values(grouped).map((req) => {
    req.lines.sort((a, b) => new Date(a.date) - new Date(b.date));
    const startDate = req.lines[0]?.date || null;
    const endDate = req.lines[req.lines.length - 1]?.date || null;
    return {
      ...req,
      startDate,
      endDate,
      // Sum of each day's Amount, not the number of day-records — a leave
      // line can be a half-day (Amount: 0.5), so lines.length overcounts
      // whenever the request includes any partial day.
      days: req.totalAmount,
      totalHours: req.totalAmount,
    };
  });
};

export default function useTeamLeaveRequests() {
  const queryClient = useQueryClient();
  const {
    members,
    isLoading: membersLoading,
    error: membersError,
  } = useTeamMembers();

  const personnelKey = members.map((m) => m.personnelNumber).join(",");

  const {
    data: requests,
    isLoading: requestsLoading,
    isFetching: requestsFetching,
    error: requestsError,
    refetch,
  } = useQuery(
    ["teamLeaveRequests", personnelKey],
    async () => {
      // Build LeaveType -> unit / description maps once.
      const unitMap = {};
      const descMap = {};
      try {
        const leaveTypesResponse = await leaveService.getLeaveTypes();
        (leaveTypesResponse.data?.value || []).forEach((lt) => {
          unitMap[lt.Type] = lt.LeaveAmountUnit;
          descMap[lt.Type] = lt.Description;
        });
      } catch {
        // maps are optional; fall back to raw codes/units
      }

      // Fetch + group requests for each team member in parallel.
      const perMember = await Promise.all(
        members.map(async (member) => {
          try {
            const response = await leaveService.getLeaveRequestsByEmployee(
              member.personnelNumber,
              { $orderby: "RequestDate desc" }
            );
            // Exclude Sick Leave before grouping so it never reaches the
            // Approvals list and its header-status lookup is skipped below.
            const records = (response.data?.value || []).filter(
              (r) => !isSickLeave(r.LeaveType, descMap)
            );
            const grouped = groupByRequestId(records, unitMap, descMap);

            // Resolve the authoritative LeaveStatus per request header.
            const withStatus = await Promise.all(
              grouped.map(async (req) => {
                let status = req.status;
                try {
                  const headerRes =
                    await leaveService.getEssLeaveRequestHeaderById(
                      req.RequestId,
                      req.dataAreaId
                    );
                  status = headerRes.data?.LeaveStatus || req.status;
                } catch {
                  // fall back to the LeaveRequestsV2 status
                }

                return {
                  id: req.RequestId,
                  RequestId: req.RequestId,
                  dataAreaId: req.dataAreaId,
                  employeeName: member.name,
                  employeeNo: member.personnelNumber,
                  leaveTypeCode: req.leaveType,
                  leaveTypeName: req.leaveTypeDescription,
                  leaveTypeDescription: req.leaveTypeDescription,
                  unit: req.unit,
                  requestedBy: member.name,
                  status,
                  reason: req.reason,
                  requestedDate: req.requestedDate,
                  startDate: req.startDate,
                  endDate: req.endDate,
                  days: req.days,
                  totalHours: req.totalHours,
                  leaveLines: req.lines,
                };
              })
            );

            return withStatus;
          } catch (err) {
            console.error(
              `❌ Failed to fetch leave requests for ${member.personnelNumber}:`,
              err
            );
            return [];
          }
        })
      );

      // Flatten and sort newest-requested first.
      return perMember
        .flat()
        .sort((a, b) => new Date(b.requestedDate) - new Date(a.requestedDate));
    },
    {
      enabled: members.length > 0,
      retry: 2,
      staleTime: 2 * 60 * 1000,
    }
  );

  const invalidate = () =>
    queryClient.invalidateQueries(["teamLeaveRequests", personnelKey]);

  const approveMutation = useMutation(
    ({ requestId, dataAreaId }) =>
      leaveService.approveLeaveRequest(requestId, dataAreaId),
    { onSuccess: invalidate }
  );

  const rejectMutation = useMutation(
    ({ requestId, dataAreaId }) =>
      leaveService.rejectLeaveRequest(requestId, dataAreaId),
    { onSuccess: invalidate }
  );

  return {
    requests: requests ?? [],
    isLoading: membersLoading || (members.length > 0 && requestsLoading),
    error: membersError || requestsError || null,
    approve: (request) =>
      approveMutation.mutateAsync({
        requestId: request.RequestId,
        dataAreaId: request.dataAreaId,
      }),
    reject: (request) =>
      rejectMutation.mutateAsync({
        requestId: request.RequestId,
        dataAreaId: request.dataAreaId,
      }),
    isMutating: approveMutation.isLoading || rejectMutation.isLoading,
    isRefreshing: requestsFetching && !requestsLoading,
    refresh: refetch,
  };
}
